import { useCallback, useMemo } from 'react';

import type { QueryClient, UseQueryResult } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getQueryClient } from 'app/get-query-client';
import { createApiClient } from 'lib/api/createApiClient';
import type { StructuredTargetVo } from 'models/milestones';
import type { GetStructuredTargetResponse, GetStructuredTargetsResponse } from 'models/reqres';
import config from 'utils/config';

import type { StructuredTargetMap } from './structuredTargetCache';
import { emptyStructuredTargetMap, mergeIntoNormalizedTargets, structuredTargetKeys } from './structuredTargetCache';
import { useRetainStructuredTargets } from './useRetainStructuredTargets';

export { seedStructuredTargetsIntoCache, structuredTargetKeys } from './structuredTargetCache';
export type { StructuredTargetMap } from './structuredTargetCache';

const api = createApiClient({
    baseURL: `${config.exchangeApiUrl}/v1`,
});

const STRUCTURED_TARGETS_BATCH_SIZE = 150;
const STRUCTURED_TARGETS_PAGE_SIZE = 1000;

function normalizeStructuredTargetIds(ids: readonly string[]): string[] {
    return Array.from(new Set(ids.filter(Boolean))).sort();
}

function chunkStructuredTargetIds(ids: readonly string[]): string[][] {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += STRUCTURED_TARGETS_BATCH_SIZE) {
        chunks.push(ids.slice(i, i + STRUCTURED_TARGETS_BATCH_SIZE));
    }
    return chunks;
}

const singleTargetArray = (target: StructuredTargetVo): StructuredTargetVo[] => [target];
const targetArray = (targets: StructuredTargetVo[]): StructuredTargetVo[] => targets;

/**
 * Imperative O(1) lookup of a structured target from the normalized cache.
 *
 * Pass the request-scoped `queryClient` (the one seeded during SSR) when reading
 * from a server-render path: `getQueryClient()` makes a fresh client on every
 * server call, so the default would never see the SSR-seeded targets and would
 * render custom-strike markets without abbreviations/team palettes until the
 * browser singleton is seeded. On the client the default browser singleton is
 * correct, so callers without a scoped client can omit it.
 */
export function getCachedStructuredTargetById(
    id: string | undefined | null,
    queryClient: QueryClient = getQueryClient(),
): StructuredTargetVo | undefined {
    if (!id) return undefined;
    const map = queryClient.getQueryData<StructuredTargetMap>(structuredTargetKeys.normalized);
    return map?.[id];
}

/** Fetches structured targets by id, batching requests, and returns them keyed by id (no cache write). */
async function fetchStructuredTargetsByIds(
    ids: readonly string[],
    signal?: AbortSignal,
): Promise<Record<string, StructuredTargetVo>> {
    const batches = chunkStructuredTargetIds(ids);
    const responses = await Promise.all(
        batches.map((batch) => {
            const params = new URLSearchParams({ page_size: '1000' });
            for (const id of batch) {
                params.append('ids', id);
            }

            // Trailing slash matters: `/structured_targets?ids=…` 301s, which axios won't replay the
            // query string for on cross-origin redirects. `/structured_targets/?ids=…` returns 200.
            return api.get<GetStructuredTargetsResponse>(`/structured_targets/?${params.toString()}`, {
                signal,
            });
        }),
    );

    const targetsById: Record<string, StructuredTargetVo> = {};
    for (const response of responses) {
        for (const target of response.data.structured_targets ?? []) {
            targetsById[target.id] = target;
        }
    }
    return targetsById;
}

/**
 * Fetch structured targets by id and map them for O(1) lookup. Targets are
 * effectively immutable once seeded (team/player/album metadata), so keep
 * them warm for the full session.
 */
export function useStructuredTargetsQuery(ids: readonly string[]): {
    data: Record<string, StructuredTargetVo> | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const queryClient = useQueryClient();
    // Sort to keep the queryKey identity stable regardless of input order, then re-derive the
    // array from its joined form so its IDENTITY is stable too. `placeholderData` below is
    // memoized by TanStack only while the option keeps the same reference
    // (`QueryObserver.createResult`: `options.placeholderData === prevResultOptions.placeholderData`),
    // so an id array that changes identity every render would re-run `readSeeded` every render,
    // hand `query.data` a fresh object every render, and make `useRetainStructuredTargets`
    // rebuild its selector and re-notify the cache on every render. Charts re-render per price
    // tick, so that is not free.
    const sortedIdsKey = normalizeStructuredTargetIds(ids).join('\u0000');
    const sortedIds = useMemo(() => (sortedIdsKey ? sortedIdsKey.split('\u0000') : []), [sortedIdsKey]);

    // Assemble the requested ids from the SSR/BFF-seeded normalized cache so this
    // `byIds` query has data on first paint instead of returning `undefined` until
    // a client request lands (the old `MilestonesStore.hydrate` path had the data
    // immediately). When every id is already seeded we use it as `initialData`
    // (targets are immutable, so no refetch); a partial hit becomes `placeholderData`
    // so the seeded targets paint while a background fetch fills the gaps.
    const readSeeded = useCallback((): Record<string, StructuredTargetVo> | undefined => {
        const normalized = queryClient.getQueryData<StructuredTargetMap>(structuredTargetKeys.normalized);
        if (!normalized) return undefined;
        const seeded: Record<string, StructuredTargetVo> = {};
        for (const id of sortedIds) if (normalized[id]) seeded[id] = normalized[id];
        return Object.keys(seeded).length > 0 ? seeded : undefined;
    }, [queryClient, sortedIds]);

    const placeholderData = useCallback(
        (prev: Record<string, StructuredTargetVo> | undefined) => prev ?? readSeeded(),
        [readSeeded],
    );

    const query = useQuery({
        queryKey: structuredTargetKeys.byIds(sortedIds),
        queryFn: async ({ signal }) => {
            const targetsById = await fetchStructuredTargetsByIds(sortedIds, signal);
            mergeIntoNormalizedTargets(queryClient, Object.values(targetsById));
            return targetsById;
        },
        enabled: sortedIds.length > 0,
        staleTime: 60 * 60 * 1000,
        initialData: () => {
            const seeded = readSeeded();
            return seeded && Object.keys(seeded).length === sortedIds.length ? seeded : undefined;
        },
        // Keep already-fetched targets rendered while pagination grows the id
        // set; consumers treat only their own missing ids as pending. Fall back
        // to the seeded cache when there's no previous query result yet.
        placeholderData,
    });
    useRetainStructuredTargets(query.data, Object.values);

    // `placeholderData` makes TanStack's own `isLoading` report `false` as soon as a
    // partial (seeded) result exists, even while the rest of `sortedIds` are still
    // in flight — recompute it ourselves from whether every requested id has landed.
    const isComplete = sortedIds.every((id) => query.data?.[id] !== undefined);
    return {
        data: query.data,
        isLoading: query.isFetching && !isComplete,
        isFetching: query.isFetching,
    };
}

/* -------------------------------------------------------------------------- */
/*              BATCHED DEMAND-DRIVEN LOADING FOR MANY SMALL READERS           */
/* -------------------------------------------------------------------------- */

/**
 * Registry that coalesces the per-row target reads on a market grid into one
 * request.
 *
 * `useStructuredTargetsQuery` keys on its own id list, so a hundred option rows
 * each asking for their one team would be a hundred cache entries and a hundred
 * requests. Those readers instead register the ids they need; the ids that are
 * not already cached are flushed together on the next macrotask — one batch for
 * everything that mounted in the same tick — and land in the shared normalized
 * map that `useStructuredTargetFromCache` subscribes to.
 *
 * Targets are immutable metadata, so an id is fetched at most once per session.
 */
type TargetDemandRegistry = {
    pending: Set<string>;
    inFlight: Set<string>;
    flushHandle: ReturnType<typeof setTimeout> | null;
};

/**
 * Keyed by the `QueryClient` the ids were registered against, not module-global.
 *
 * The scheduled flush closes over one client, so a single shared registry would
 * let a warm server drain ids registered by one request into another request's
 * cache — the cross-request bleed `AGENTS.md` warns about — and would keep that
 * request's whole query cache alive until the timer fired. A `WeakMap` makes the
 * batch state request-scoped and collectable with the client that owns it.
 */
const targetDemandRegistries = new WeakMap<QueryClient, TargetDemandRegistry>();

function registryFor(queryClient: QueryClient): TargetDemandRegistry {
    let registry = targetDemandRegistries.get(queryClient);
    if (!registry) {
        registry = { pending: new Set(), inFlight: new Set(), flushHandle: null };
        targetDemandRegistries.set(queryClient, registry);
    }
    return registry;
}

async function flushStructuredTargetDemand(queryClient: QueryClient): Promise<void> {
    const registry = registryFor(queryClient);
    registry.flushHandle = null;
    // Read the cache once here rather than per registering row: seeding can land
    // between registration and flush, and this is the authoritative filter.
    const cached = queryClient.getQueryData<StructuredTargetMap>(structuredTargetKeys.normalized);
    const ids = [...registry.pending].filter((id) => !cached?.[id]);
    registry.pending.clear();
    if (ids.length === 0) return;

    for (const id of ids) registry.inFlight.add(id);
    try {
        const targetsById = await fetchStructuredTargetsByIds(ids);
        mergeIntoNormalizedTargets(queryClient, Object.values(targetsById));
    } catch (error) {
        // Non-fatal: the rows that wanted these targets render without their
        // extra detail. Logged (not swallowed) because a persistent failure here
        // silently strips content — e.g. team records — off every market card.
        console.error('[structuredTargets] batched by-id fetch failed', { ids, error });
    } finally {
        // Clearing on failure too, so a failed id can be retried by a later mount.
        for (const id of ids) registry.inFlight.delete(id);
    }
}

/**
 * Declares that `ids` need resolving, without owning a query for them. Read the
 * results back with {@link useStructuredTargetFromCache}.
 *
 * A plain function rather than a hook so callers can drive it from their own
 * `useEffect`, matching every other cache helper in this module
 * (`seedStructuredTargetsIntoCache`, `prefetchStructuredTargetsByIds`) — the
 * `QueryClient` comes in, and all state lives against it.
 *
 * Safe to call for every row of a grid: registration is idempotent, and the
 * flush drops any id that has since been cached. Deliberately does no cache read
 * of its own — one `getQueryData` (and its query-key hash) per row per mount to
 * pre-empt a decision the flush remakes anyway is not worth paying.
 */
export function ensureStructuredTargets(queryClient: QueryClient, ids: readonly string[]): void {
    const registry = registryFor(queryClient);
    let added = false;
    for (const id of ids) {
        if (!id || registry.inFlight.has(id)) continue;
        registry.pending.add(id);
        added = true;
    }
    if (!added || registry.flushHandle) return;
    registry.flushHandle = setTimeout(() => void flushStructuredTargetDemand(queryClient), 0);
}

/**
 * O(1) read of one structured target from the shared normalized map.
 *
 * A pure selector — it never fetches (see `ensureStructuredTargets` for that).
 * `select` plus `notifyOnChangeProps: ['data']` is what keeps a grid of readers
 * from re-rendering when a *sibling's* target lands: `mergeIntoNormalizedTargets`
 * preserves target identities, so an unaffected row's selected value stays
 * reference-equal and its observer reports no change. Keep the options stable
 * (module-level `queryFn`, memoized `select`) — a fresh identity per render makes
 * React Query re-run the selector and re-notify the cache on every render.
 */
export function useStructuredTargetFromCache(id: string | undefined | null): StructuredTargetVo | undefined {
    const select = useCallback((map: StructuredTargetMap) => (id ? map[id] : undefined), [id]);
    const { data } = useQuery({
        queryKey: structuredTargetKeys.normalized,
        queryFn: emptyStructuredTargetMap,
        enabled: false,
        notifyOnChangeProps: ['data'],
        select,
    });
    return data;
}

/**
 * Imperatively warms the structured-targets cache by id and merges into the
 * normalized map. For non-React preloads (e.g. client-side event switches) —
 * mirrors `MilestonesStore.loadCustomStrikeStructuredTargets`.
 */
export async function prefetchStructuredTargetsByIds(queryClient: QueryClient, ids: readonly string[]): Promise<void> {
    const sortedIds = normalizeStructuredTargetIds(ids);
    if (!sortedIds.length) return;
    await queryClient.prefetchQuery({
        queryKey: structuredTargetKeys.byIds(sortedIds),
        queryFn: async ({ signal }) => {
            const targetsById = await fetchStructuredTargetsByIds(sortedIds, signal);
            mergeIntoNormalizedTargets(queryClient, Object.values(targetsById));
            return targetsById;
        },
        staleTime: 60 * 60 * 1000,
    });
}

/** Fetch a single structured target by id. Mirrors `MilestonesStore.loadStructuredTarget`. */
export async function fetchStructuredTarget(id: string, signal?: AbortSignal): Promise<StructuredTargetVo> {
    const response = await api.get<GetStructuredTargetResponse>(`/structured_targets/${id}`, { signal });
    return response.data.structured_target;
}

export function useStructuredTargetQuery(id: string | null | undefined): UseQueryResult<StructuredTargetVo, Error> {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: structuredTargetKeys.byId(id ?? ''),
        queryFn: async ({ signal }) => {
            const target = await fetchStructuredTarget(id as string, signal);
            mergeIntoNormalizedTargets(queryClient, [target]);
            return target;
        },
        enabled: !!id,
        staleTime: 60 * 60 * 1000,
    });
    useRetainStructuredTargets(query.data, singleTargetArray);
    return query;
}

/**
 * Fetches the entire structured-targets catalog, following pagination cursors
 * internally. Mirrors `MilestonesStore.loadStructuredTargets`. Single call site
 * today (`SuperbowlHero`) — everything else fetches by id via
 * `useStructuredTargetsQuery`.
 */
export async function fetchAllStructuredTargets(signal?: AbortSignal): Promise<StructuredTargetVo[]> {
    const structuredTargets: StructuredTargetVo[] = [];
    let cursor: string | undefined;

    do {
        const params = new URLSearchParams({ page_size: String(STRUCTURED_TARGETS_PAGE_SIZE) });
        if (cursor) params.set('cursor', cursor);
        const response = await api.get<GetStructuredTargetsResponse>(`/structured_targets/?${params.toString()}`, {
            signal,
        });
        structuredTargets.push(...response.data.structured_targets);
        cursor = response.data.cursor;
    } while (cursor);

    return structuredTargets;
}

export function useAllStructuredTargetsQuery(enabled = true): UseQueryResult<StructuredTargetVo[], Error> {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: structuredTargetKeys.all,
        queryFn: async ({ signal }) => {
            const targets = await fetchAllStructuredTargets(signal);
            // Also merge into the normalized map so imperative `getCachedStructuredTargetById`
            // reads (e.g. BinaryMarket color/abbreviation getters) resolve, as the by-id paths do.
            mergeIntoNormalizedTargets(queryClient, targets);
            return targets;
        },
        enabled,
        staleTime: 60 * 60 * 1000,
    });
    useRetainStructuredTargets(query.data, targetArray);
    return query;
}
